import InputField from 'components/input/InputField';
import React, { useEffect, useState } from 'react';
import { RoleTypeUpdateDto, UserInfoDto, UserProfileDto } from 'services/data-contracts';
import styled from 'styled-components';
import { useStoreWrapper } from 'stores/StoreHelper';
import { Row, Button, useModelStateContext, SaladToggleButton } from 'saladsoft-primitive';
import ServiceHelper from 'stores/ServiceHelper';
import { toast } from 'react-toastify';
import { validationHelper } from 'controls/validationHelper';

const AdminUserForm = ({ adminUser, onSave }: { adminUser?: UserInfoDto; onSave: () => void }) => {
  const { uiStore } = useStoreWrapper();

  const [isValid, setIsValid] = useState<boolean>(false);

  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [passwordError, setPasswordError] = useState<boolean>(false);

  const [showPassword, setShowPassword] = useState<boolean>(false); // 비밀번호 변경 모드 토글

  const DEFAULT_COUNTRY_CODE = '+82';

  const isEdit = Boolean(adminUser?.id);

  const { stateModel, setState, setMultipleState, unmountModel } = useModelStateContext<any>();

  // 렌더링 시 사용되는 비밀번호 유효성 검사(값이 존재하고, 서로 일치해야 true)
  const isPasswordValid =
    !!stateModel?.password && !!passwordConfirm && stateModel?.password === passwordConfirm;

  const validate = () => {
    const firstName = validationHelper.firstName(stateModel?.firstName);
    const lastName = validationHelper.lastName(stateModel?.lastName);
    const username = validationHelper.email(stateModel?.username);
    const countryCode = validationHelper.countryCode(stateModel?.countryCode);
    const phoneNumber = validationHelper.phoneNumber(stateModel?.phoneNumber);
    const birthday = validationHelper.birthday(stateModel?.birthday);
    const isBirthdayValid = validationHelper.isValidBirthday(stateModel?.birthday);

    setIsValid(
      firstName &&
      lastName &&
      username &&
      countryCode &&
      phoneNumber &&
      birthday &&
      isBirthdayValid,
    );
  };

  useEffect(() => {
    validate();
  }, [stateModel]);

  // 초기화
  useEffect(() => {
    if (isEdit) {
      // 관리자 정보 수정
      const profileData: UserProfileDto = {
        firstName: adminUser?.profile?.firstName || '',
        lastName: adminUser?.profile?.lastName || '',
        countryCode: adminUser?.profile?.countryCode || DEFAULT_COUNTRY_CODE,
        phoneNumber: adminUser?.profile?.phoneNumber || '',
        birthday: adminUser?.profile?.birthday || '',
      };
      setMultipleState(profileData);
    } else {
      // 관리자 계정 생성
      setMultipleState({
        username: '',
        password: '',
        profile: {
          firstName: '',
          lastName: '',
          countryCode: DEFAULT_COUNTRY_CODE,
          phoneNumber: '',
          birthday: '1900-01-01',
        },
      });
    }

    return () => {
      unmountModel();
    };
  }, [adminUser]);

  const handleRoleTypeChange = () => {
    ServiceHelper.adminMemberService?.changeMemberRoleType(adminUser?.id as number, {
      roleType: stateModel?.roleType as RoleTypeUpdateDto['roleType'],
    });
  };

  // 관리자 계정 수정 및 생성 함수
  const handleUpdate = () => {
    if (!isEdit || showPassword) {
      if (!isPasswordMatch()) {
        setPasswordError(true);
        return;
      }
    }

    if (isEdit) {
      // 관리자 정보 수정
      const profileData: UserProfileDto = {
        firstName: stateModel?.firstName || '',
        lastName: stateModel?.lastName || '',
        countryCode: stateModel?.countryCode || DEFAULT_COUNTRY_CODE,
        phoneNumber: stateModel?.phoneNumber || '',
        birthday: stateModel?.birthday || '',
      };

      if (adminUser?.roleType !== stateModel?.roleType) {
        handleRoleTypeChange();
      }

      ServiceHelper.adminMemberService?.saveMemberProfile(adminUser?.id as number, profileData)
        .then((res) => {
          if (res.status === 200 || res.status === 201) {
            toast.success('관리자 정보가 수정되었습니다.');
            onSave();
            uiStore?.modal.close();
          } else {
            toast.error('관리자 정보 수정에 실패했습니다.');
          }
        });
    } else {
      // 관리자 계정 생성
      const signupData = { ...stateModel };

      if (!signupData.profile) {
        signupData.profile = {
          firstName: '',
          lastName: '',
          countryCode: DEFAULT_COUNTRY_CODE,
          phoneNumber: '',
          birthday: '1900-01-01',
        };
      }

      ServiceHelper.adminMemberService?.registerAdminMember(signupData).then((res) => {
        if (res.status === 200 || res.status === 201) {
          toast.success('관리자 계정이 생성되었습니다.');
          onSave();
          uiStore?.modal.close();
        } else {
          if (res.status === 409) {
            toast.error('이미 존재하는 이메일입니다.');
          } else {
            toast.error('관리자 계정 생성에 실패했습니다.');
          }
        }
      });
    }
  };

  // 관리자 계정 비활성화
  const handleDelete = (isActive: boolean) => {
    // 현재 비활성화 상태이면 isActive가 true, 활성화 상태이면 isActive가 false
    if (adminUser?.id) {
      uiStore?.confirm.show({
        title: isActive ? '관리자 계정 활성화' : '관리자 계정 비활성화',
        message: `${adminUser.username} 계정을 \n${isActive ? '활성화' : '비활성화'} 하시겠습니까?`,
        onConfirmed: () => {
          if (!isActive) {
            // 현재 활성화 상태 (isActive = false)
            ServiceHelper.adminMemberService
              ?.withdrawAdminMember(adminUser?.id as number)
              .then((res) => {
                toast.success('관리자 계정이 활성화 되었습니다.');
                uiStore?.modal.close();
                onSave?.();
              });
          } else {
            // 현재 비활성화 상태 (isActive = true)
            ServiceHelper.adminMemberService
              ?.unwithdrawAdminMember(adminUser?.id as number)
              .then((res) => {
                toast.success('관리자 계정이 비활성화 되었습니다.');
                uiStore?.modal.close();
                onSave?.();
              });
          }
        },
      });
    }
  };

  // 비밀번호 일치 여부 확인 (버튼 클릭 이벤트 전용)
  const isPasswordMatch = () => {
    if (stateModel?.password && passwordConfirm) {
      const isMatch = stateModel.password === passwordConfirm;

      if (!isMatch) {
        setPasswordError(true);
      }
      return isMatch;
    }
    return false;
  };

  // 비밀번호 변경 모드 토글
  const handleTogglePasswordSection = () => {
    setShowPassword(!showPassword);
    if (!showPassword) {
      setState('password', '');
      setPasswordConfirm('');
      setPasswordError(false);
    }
  };

  // 비밀번호 변경 적용
  const applyPasswordChange = () => {
    if (!isPasswordMatch()) {
      setPasswordError(true);
      return;
    }

    ServiceHelper.adminMemberService
      ?.changePassword(adminUser?.id as number, {
        newPassword: stateModel?.password || '',
      })
      .then((res) => {
        toast.success('비밀번호가 변경되었습니다.');
        setShowPassword(false);
        onSave();
      });
  };

  // 비밀번호 변경 취소
  const cancelPasswordChange = () => {
    setState('password', '');
    setPasswordConfirm('');
    setPasswordError(false);
  };

  return (
    <>
      <FormContainer $isEdit={isEdit}>
        {!isEdit && <Notice>* 모든 입력값은 필수입니다.</Notice>}

        <InputField
          label="이메일"
          value={isEdit ? adminUser?.username : stateModel?.username}
          onChange={(value) => {
            isEdit ? null : setState('username', value);
          }}
          disabled={isEdit}
        />
        <Row gap={10} width="100%" alignItems="space-between">
          <InputField
            label="성"
            value={isEdit ? stateModel?.lastName || '' : stateModel?.profile?.lastName || ''}
            onChange={(value) => {
              if (isEdit) {
                setState('lastName', value);
              } else {
                setState('profile', {
                  ...stateModel?.profile,
                  lastName: value,
                });
              }
            }}
            disabled={isEdit && showPassword}
          />
          <InputField
            label="이름"
            value={isEdit ? stateModel?.firstName || '' : stateModel?.profile?.firstName || ''}
            onChange={(value) => {
              if (isEdit) {
                setState('firstName', value);
              } else {
                setState('profile', {
                  ...stateModel?.profile,
                  firstName: value,
                });
              }
            }}
            disabled={isEdit && showPassword}
          />
        </Row>
        <InputField
          label="전화번호"
          value={isEdit ? stateModel?.phoneNumber || '' : stateModel?.profile?.phoneNumber || ''}
          onChange={(value) => {
            if (isEdit) {
              setState('phoneNumber', value);
            } else {
              setState('profile', {
                ...stateModel?.profile,
                phoneNumber: value,
              });
            }
          }}
          disabled={isEdit && showPassword}
        />

        {/* 비밀번호 변경 텍스트 표시 */}
        {isEdit && (
          <ChangePasswordButton onClick={handleTogglePasswordSection}>
            {showPassword ? '비밀번호 변경 취소' : '비밀번호 변경'}
          </ChangePasswordButton>
        )}

        {(!isEdit || showPassword) && (
          <Row gap={10} width="100%" alignItems="flex-start">
            <InputField
              label="비밀번호"
              type="password"
              value={stateModel?.password || ''}
              onChange={(value) => {
                setState('password', value);
                setPasswordError(false);
              }}
              onBlur={isPasswordMatch}
              className={isEdit && showPassword ? 'password-focus' : ''}
            />
            <InputField
              label="비밀번호 확인"
              type="password"
              value={passwordConfirm}
              onChange={(value) => {
                setPasswordConfirm(value);
                setPasswordError(false);
              }}
              onBlur={isPasswordMatch}
              error={passwordError}
              helperText={passwordError ? '비밀번호가 일치하지 않습니다' : ''}
              className={isEdit && showPassword ? 'password-focus' : ''}
            />
            <>
              {showPassword && (
                <ButtonContainer className={showPassword ? 'show' : ''}>
                  <Button
                    type="contained"
                    onClick={applyPasswordChange}
                    className="change-apply-button"
                    isValid={isPasswordValid}
                  >
                    적용
                  </Button>
                  <Button
                    type="outlined"
                    onClick={cancelPasswordChange}
                    className="cancel-apply-button"
                  >
                    초기화
                  </Button>
                </ButtonContainer>
              )}
            </>
          </Row>
        )}

        {isEdit && showPassword && (
          <Notice className="password-change-notice" style={{ marginTop: 10 }}>
            * 비밀번호 변경 중입니다. 다른 정보는 비밀번호 변경을 완료하거나 취소한 후 수정할 수
            있습니다.
          </Notice>
        )}
        <SaladToggleButton
          defaultIndex={adminUser?.roleType === 'ADMIN' ? 0 : 1}
          items={[
            { label: '관리자', value: 'ADMIN' },
            { label: '매니저', value: 'MANAGER' },
          ]}
          onChange={(value) => {
            setState('roleType', value);
          }}
        />

        <ModifyButtonsWrapper isWithdrawn={adminUser?.isWithdrawn || false}>
          <ModifyButtonsContainer>
            <ModifyButtonsGroup>
              <Button
                type="outlined"
                onClick={() => uiStore?.modal.close()}
                className="cancel-button"
              >
                취소
              </Button>
              <Button
                type="contained"
                onClick={handleUpdate}
                className="save-button"
                isValid={!showPassword}
              >
                {isEdit ? '수정하기' : '생성하기'}
              </Button>
            </ModifyButtonsGroup>
            {isEdit && (
              <Button
                type="contained"
                onClick={() => handleDelete(adminUser?.isWithdrawn || false)}
                className="delete-button"
                isValid={!showPassword}
              >
                {adminUser?.isWithdrawn ? '활성화' : '비활성화'}
              </Button>
            )}
          </ModifyButtonsContainer>
        </ModifyButtonsWrapper>
      </FormContainer>
    </>
  );
};

const FormContainer = styled.div<{ $isEdit: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: ${({ $isEdit }) => ($isEdit ? '20px' : '0 20px')};
`;

const Notice = styled.div`
  color: #d32f2f;
  font-size: 12px;
  margin-bottom: 10px;
`;

// 비밀번호 변경 텍스트 버튼
const ChangePasswordButton = styled.span`
  display: inline-block;
  align-self: flex-end;
  font-size: 14px;
  cursor: pointer;
  padding: 5px 0;
  text-decoration: underline;
  width: fit-content;
  margin-left: auto;
`;

const ModifyButtonsWrapper = styled.div<{ isWithdrawn: boolean }>`
  transform: translateY(-5px);
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
`;

const ModifyButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 20px;

  .save-button,
  .cancel-button,
  .delete-button {
    height: 40px;
  }
`;

const ModifyButtonsGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-right: auto;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  align-self: flex-start;
  margin-top: 25px;
  height: 40px;

  opacity: 0;
  transform: translateY(-5px);
  pointer-events: none;
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;

  &.show {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }

  .change-apply-button,
  .cancel-apply-button {
    white-space: nowrap;
    height: 40px;
  }
`;

export default AdminUserForm;
